import { createStore } from 'vuex'
import DynamicRightSlotData from "../models/dynamicrighslotdata"
import DynamicModalData from "../models/dynamicmodaldata"
import LatLngPoint from "../models/latlngpoint"
import MunicipalitiesAndRegionsModel from "../models/municipalitiesandregionsmodel"
import RadioButtonModel from "@/models/radiobuttonmodel"
import TableData from "@/models/tabledata"
import ApiDataModel from "@/models/apidatamodel"
import { DataTransferDirection } from "../models/datatransfermodel"
import OperatorModel from "@/models/operatormodel"
import i18n from '@/i18n'

const loadingEvent: { [name: string]: boolean } = {}
const dataToLoad: { url: string, type: string, params?: URLSearchParams } = { url: "", type: "" }
const visiblemarkers: Map<string, number> | null = null
const markersData: Map<string, string> | null = null
const pageWhitelist: Map<string, string> | null = null
const legendLimits: Array<number> | null = null
const subTopic: string | null = null

export const store = createStore({
    state: {
        isLoading: false,
        baseHref: "", // Subdomain where the software is running ie. "/ContactForms"
        loadingEvents: loadingEvent, // a dictionary that keeps track of loading (setLoading spinner) elements
        rightSlot: "",
        mapServicesUrl: "",
        activeLang: "fi", // Current language, default fi
        contentServiceUrl: "",
        contentServiceTimeStamp: "",
        displayType: "xl",
        displayExtraSmall: "xs",
        displaySmall: "s",
        displayMedium: "m",
        displayLarge: "l",
        displayExtraLarge: "xl",
        modalComponentName: "",
        mainLoaded: false,
        locateMeClick: false,
        locationElementVisible: false,
        stopLocate: false,
        skipToContentClicked: false,
        searchAddressSelected: null,
        dropDownSelected: null,
        dataPublishServicesUrl: "",
        municipalitiesData: MunicipalitiesAndRegionsModel.empty(),
        regionsData: MunicipalitiesAndRegionsModel.empty(),
        dataUrlToLoad: dataToLoad,
        rightSlotData: DynamicRightSlotData.empty(),
        legend: "",
        header: "",
        visibleMarkers: visiblemarkers,
        markersData,
        modalData: DynamicModalData.empty(),
        coordinates: LatLngPoint,
        currentPage: "",
        layersVisible: true,
        selectedAreaRadioButton: "", // Kunta/Maakunta/Koko Suomi radiobuttongroupin valinta
        selectedMunicipality: "", // Kunta valintalistan valinta
        selectedProvince: "", // Maakunta valintalistan valinta
        infokarttaRadioValues: [],
        liikenneInfokarttaRadioValues: [],
        laajakaistaJaPuhelinInfokarttaRadioValues: [],
        postiInfokarttaRadioValues: [],
        selectedSubjectRadioButton: RadioButtonModel.createEmpty(),
        twoColumnsMobileFooter: false, // Käytetäänkö mobiilinäkymän footerissa kahta tekstipalstaa
        dataYear: "", // Vuosi, jota data koskee
        pageWhitelist,
        legendLimits,
        iframeTitle: "",
        tableData: TableData.empty(),
        selectedDropDownResourceKey: "", // RightSlotissa näytettävä dropdownin valinta
        subTopic,
        apiData: ApiDataModel.createEmpty(),
        locationSearchMinZoom: 0,
        frequencySearchData: { lat: "0", lng: "0", f1: "", f2: "", bw: "" },
        radiomikrofonitMinFrequency: 0, // Radiomikrofonien taajuushaun sallitun taajuusvälin alaraja
        radiomikrofonitMaxFrequency: 0, // Radiomikrofonien taajuushaun sallitun taajuusvälin yläraja
        frequencySearcValidationMessage: "", // Kielitermiavain taajuushaun validointiviesteille
        timeOfDay: 0, // Bittimittarissa valittu vuorokaudenaika
        dataTransferDirection: DataTransferDirection.DOWNLOAD, // Bittimittarissa valittu datan siirtosuunta. Download/upload.
        operatorId: 0, // Bittimittarissa valitun operaattorin id
        operatorList: OperatorModel.emptyList(), // Bittimittarissa näytettävät operaattorit
        minMaxRadioMFrequencies: {
            min: 0,
            max: 0
        }, // Radiomikrofonitaajuuksien minimi- ja maximiarvot
        radiomikrofonitBandwidth: 0, // Radiomikrofonien taajuushaussa käytettävä kaistanleveys (Haettu RadiomikrofonitServiceltä)
        messages: {
            fi: {},
            sv: {},
            en: {}
        }
    },
    getters: {
        isLoading: (state) => {
            return state.isLoading
        },
        baseHref: (state) => {
            return state.baseHref
        },
        rightSlot: (state) => {
            return state.rightSlot
        },
        activeLang: (state) => {
            return state.activeLang
        },
        contentServiceUrl: (state) => {
            return state.contentServiceUrl
        },
        contentServiceTimeStamp: (state) => {
            return state.contentServiceTimeStamp
        },
        mapServicesUrl: (state) => {
            return state.mapServicesUrl
        },
        displayType: (state) => {
            return state.displayType
        },
        modalComponentName: (state) => {
            return state.modalComponentName
        },
        mainLoaded: (state) => {
            return state.mainLoaded
        },
        locateMeClick: (state) => {
            return state.locateMeClick
        },
        locationElementVisible: (state) => {
            return state.locationElementVisible
        },
        stopLocate: (state) => {
            return state.stopLocate
        },
        skipToContentClicked: (state) => {
            return state.skipToContentClicked
        },
        searchAddressSelected: (state) => {
            return state.searchAddressSelected
        },
        dropDownSelected: (state) => {
            return state.dropDownSelected
        },
        dataPublishServicesUrl: (state) => {
            return state.dataPublishServicesUrl
        },
        dataUrlToLoad: (state) => {
            return state.dataUrlToLoad
        },
        rightSlotData: (state) => {
            return state.rightSlotData
        },
        municipalitiesData: (state) => {
            return state.municipalitiesData
        },
        regionsData: (state) => {
            return state.regionsData
        },
        legend: (state) => {
            return state.legend
        },
        header: (state) => {
            return state.header
        },
        visibleMarkers: (state) => {
            return state.visibleMarkers
        },
        markersData: (state) => {
            return state.markersData
        },
        coordinates: (state) => {
            return state.coordinates
        },
        currentPage: (state) => {
            return state.currentPage
        },
        modalData: (state) => {
            return state.modalData
        },
        selectedAreaRadioButton: (state) => {
            return state.selectedAreaRadioButton
        },
        selectedMunicipality: (state) => {
            return state.selectedMunicipality
        },
        selectedProvince: (state) => {
            return state.selectedProvince
        },
        layersVisible: (state) => {
            return state.layersVisible
        },
        infokarttaRadioValues: (state) => {
            return state.infokarttaRadioValues
        },
        liikenneInfokarttaRadioValues: (state) => {
            return state.liikenneInfokarttaRadioValues
        },
        laajakaistaJaPuhelinInfokarttaRadioValues: (state) => {
            return state.laajakaistaJaPuhelinInfokarttaRadioValues
        },
        postiInfokarttaRadioValues: (state) => {
            return state.postiInfokarttaRadioValues
        },
        selectedSubjectRadioButton: (state) => {
            return state.selectedSubjectRadioButton
        },
        twoColumnsMobileFooter: (state) => {
            return state.twoColumnsMobileFooter
        },
        dataYear: (state) => {
            return state.dataYear
        },
        pageWhitelist: (state) => {
            return state.pageWhitelist
        },
        legendLimits: (state) => {
            return state.legendLimits
        },
        iframeTitle: (state) => {
            return state.iframeTitle
        },
        selectedDropDownResourceKey: (state) => {
            return state.selectedDropDownResourceKey
        },
        tableData: (state) => {
            return state.tableData
        },
        subTopic: (state) => {
            return state.subTopic
        },
        apiData: (state) => {
            return state.apiData
        },
        locationSearchMinZoom: (state) => {
            return state.locationSearchMinZoom
        },
        frequencySearchData: (state) => {
            return state.frequencySearchData
        },
        radiomikrofonitMinFrequency: (state) => {
            return state.radiomikrofonitMinFrequency
        },
        radiomikrofonitMaxFrequency: (state) => {
            return state.radiomikrofonitMaxFrequency
        },
        frequencySearcValidationMessage: (state) => {
            return state.frequencySearcValidationMessage
        },
        timeOfDay: (state) => {
            return state.timeOfDay
        },
        dataTransferDirection: (state) => {
            return state.dataTransferDirection
        },
        operatorList: (state) => {
            return state.operatorList
        },
        operatorId: (state) => {
            return state.operatorId
        },
        minMaxRadioMFrequencies: (state) => {
            return state.minMaxRadioMFrequencies
        },
        radiomikrofonitBandwidth: (state) => {
            return state.radiomikrofonitBandwidth
        },
        messages: (state) => {
            return state.messages
        }
    },
    mutations: {
        // Asettaa, pitääkö sivulla näkyä "loading" - spinneri
        setLoading(state, loadingEvent: { name: string, value: boolean }) {
            // Jokaiselle toiminnolle on oma "loading" event, jotka pidetään tallessa sisäisessä oliossa.
            // Tämä jotta yksittäisen toiminnon "loading" -  ei piilota toisen sivun tai toimminnon "loading" - spinneria.
            // sen jälkeen kun kyseinen event on ns.valmis.
            state.loadingEvents[loadingEvent.name] = loadingEvent.value

            // Jos yksikin näistä em. eventeistä sisältää true arvon, se tarkoittaa
            // että "loading" spinneri, pitää näkyä.
            // state.isLoading määritys tehdän täällä getterin sijasta,
            // koska vue ei pysty havannoimaan taulukon sisäisiä muutoksia ilman mutaatio / action - kutsua.
            let isLoading = false
            for (const key in state.loadingEvents) {
                const value = state.loadingEvents[key]

                if (value) {
                    isLoading = true
                    break
                }
            }

            state.isLoading = isLoading
        },
        setBaseHref(state, baseHref: string) {
            if (state.baseHref === "") { // BaseHref can only be assigned once
                state.baseHref = baseHref
            }
        },
        setRightSlot(state, slotName: string) {
            state.rightSlot = slotName
            // Nollataan rightslotdata kun asetaan uusi rightslot
            state.rightSlotData = []
            // poistetaan legenda
            state.legend = ""
        },
        setLang(state, lang: string) {
            if (lang === "fi" || lang === "sv" || lang === "en") {
                state.activeLang = lang
                i18n.global.locale.value = lang
            }
        },
        setContentServiceUrl(state, newUrl: string) {
            state.contentServiceUrl = newUrl
        },
        setContentServiceTimeStamp(state, newUrl: string) {
            state.contentServiceTimeStamp = newUrl
        },
        setMapServicesUrl(state, url) {
            state.mapServicesUrl = url
        },
        setDisplayType(state, type) {
            state.displayType = type
        },
        setModalComponentName(state, name) {
            state.modalComponentName = name
        },
        setMainLoaded(state, bit) {
            state.mainLoaded = bit
        },
        setLocateMeClick: (state) => {
            state.locateMeClick = !state.locateMeClick
        },
        setLocationElementVisible: (state, data) => {
            state.locationElementVisible = data
        },
        setStopLocate: (state) => {
            state.stopLocate = !state.stopLocate
        },
        setSkipToContentClicked: (state, data) => {
            state.skipToContentClicked = data
        },
        setSearchAddressSelected: (state, selected) => {
            state.searchAddressSelected = selected
        },
        setDropDownSelected: (state, selected) => {
            state.dropDownSelected = selected
        },
        setDataUrlToLoad: (state, url) => {
            state.dataUrlToLoad = url
        },
        setDataPublishServicesUrl: (state, url) => {
            state.dataPublishServicesUrl = url
        },
        setRightSlotData(state, data: DynamicRightSlotData) {
            state.rightSlotData = data
        },
        setMunicipalitiesData: (state, json) => {
            state.municipalitiesData = json
        },
        setRegionsData: (state, json) => {
            state.regionsData = json
        },
        setLegend: (state, data) => {
            state.legend = data
        },
        setHeader: (state, data) => {
            state.header = data
        },
        setVisibleMarkers: (state, data) => {
            state.visibleMarkers = data
        },
        setMarkersData: (state, data) => {
            state.markersData = data
        },
        setCoordinates: (state, data) => {
            state.coordinates = data
        },
        setCurrentPage: (state, data) => {
            state.currentPage = data
        },
        setModalData: (state, data) => {
            state.modalData = data
        },
        setSelectedAreaRadioButton: (state, data) => {
            state.selectedAreaRadioButton = data
        },
        setSelectedMunicipality: (state, data) => {
            state.selectedMunicipality = data
        },
        setSelectedProvince: (state, data) => {
            state.selectedProvince = data
        },
        setLayersVisible: (state, data) => {
            state.layersVisible = data
        },
        setInfokarttaRadioValues: (state, data) => {
            state.infokarttaRadioValues = data
        },
        setLiikenneInfokarttaRadioValues: (state, data) => {
            state.liikenneInfokarttaRadioValues = data
        },
        setLaajakaistaJaPuhelinInfokarttaRadioValues: (state, data) => {
            state.laajakaistaJaPuhelinInfokarttaRadioValues = data
        },
        setPostiInfokarttaRadioValues: (state, data) => {
            state.postiInfokarttaRadioValues = data
        },
        setSelectedSubjectRadioButton: (state, data) => {
            state.selectedSubjectRadioButton = data
        },
        setTwoColumnsMobileFooter: (state, data) => {
            state.twoColumnsMobileFooter = data
        },
        setDataYear: (state, data) => {
            state.dataYear = data
        },
        setPageWhitelist: (state, data) => {
            state.pageWhitelist = data
        },
        setLegendLimits: (state, data) => {
            state.legendLimits = data
        },
        setIframeTitle: (state, data) => {
            state.iframeTitle = data
        },
        setSelectedDropDownResourceKey: (state, data) => {
            state.selectedDropDownResourceKey = data
        },
        setTableData: (state, data) => {
            state.tableData = data
        },
        setSubTopic: (state, data) => {
            state.subTopic = data
        },
        setApiData: (state, data) => {
            state.apiData = data
        },
        setLocationSearchMinZoom: (state, zoom) => {
            state.locationSearchMinZoom = Number(zoom)
        },
        setFrequencySearchData: (state, data: {lat, lng, f1, f2, bw}) => {
            state.frequencySearchData.lat = data.lat
            state.frequencySearchData.lng = data.lng
            state.frequencySearchData.f1 = data.f1 ?? ""
            state.frequencySearchData.f2 = data.f2 ?? ""
            state.frequencySearchData.bw = data.bw ?? ""
        },
        setRadiomikrofonitMinFrequency: (state, data) => {
            state.radiomikrofonitMinFrequency = data
        },
        setRadiomikrofonitMaxFrequency: (state, data) => {
            state.radiomikrofonitMaxFrequency = data
        },
        setFrequencySearcValidationMessage: (state, data) => {
            state.frequencySearcValidationMessage = data
        },
        setTimeOfDay: (state, data) => {
            state.timeOfDay = data
        },
        setDataTransferDirection: (state, data) => {
            state.dataTransferDirection = data
        },
        setOperatorList: (state, data) => {
            state.operatorList = data
        },
        setOperatorId: (state, data) => {
            state.operatorId = data
        },
        setMinMaxRadioMFrequencies: (state, data) => {
            state.minMaxRadioMFrequencies = data
        },
        setRadiomikrofonitBandwidth: (state, data) => {
            state.radiomikrofonitBandwidth = data
        },
        setMessages: (state, data) => {
            state.messages = data
        }
    },
    actions: {
        setLoading(state, isLoading: boolean) {
            state.commit("setLoading", isLoading)
        },
        setBaseHref(state, baseHref: string) {
            state.commit("setBaseHref", baseHref)
        },
        setRightSlot(state, slotName: string) {
            state.commit("setRightSlot", slotName)
        },
        setLang(state, lang: string) {
            state.commit("setLang", lang)
        },
        setContentServiceUrl(state, newUrl: string) {
            state.commit("setContentServiceUrl", newUrl)
        },
        setContentServiceTimeStamp(state, newUrl: string) {
            state.commit("setContentServiceTimeStamp", newUrl)
        },
        setMapServicesUrl(state, url) {
            state.commit("setMapServicesUrl", url)
        },
        setDisplayType(state, type) {
            state.commit("setDisplayType", type)
        },
        setModalComponentName(state, name) {
            state.commit("setModalComponentName", name)
        },
        setMainLoaded(state, bit) {
            state.commit("setMainLoaded", bit)
        },
        setLocateMeClick: (state) => {
            state.commit("setLocateMeClick")
        },
        setLocationElementVisible: (state, data) => {
            state.commit("setLocationElementVisible", data)
        },
        setStopLocate: (state) => {
            state.commit("setStopLocate")
        },
        setSkipToContentClicked: (state, data) => {
            state.commit("setSkipToContentClicked", data)
        },
        setSearchAddressSelected: (state, selected) => {
            state.commit("setSearchAddressSelected", selected)
        },
        setDropDownSelected: (state, selected) => {
            state.commit("setDropDownSelected", selected)
        },
        setDataUrlToLoad: (state, url) => {
            state.commit("setDataUrlToLoad", url)
        },
        setDataPublishServicesUrl: (state, url) => {
            state.commit("setDataPublishServicesUrl", url)
        },
        setRightSlotData: (state, data) => {
            state.commit("setRightSlotData", data)
        },
        setMunicipalitiesData: (state, data) => {
            state.commit("setMunicipalitiesData", data)
        },
        setRegionsData: (state, data) => {
            state.commit("setRegionsData", data)
        },
        setLegend: (state, data) => {
            state.commit("setLegend", data)
        },
        setHeader: (state, data) => {
            state.commit("setHeader", data)
        },
        setVisibleMarkers: (state, data) => {
            state.commit("setVisibleMarkers", data)
        },
        setMarkersData: (state, data) => {
            state.commit("setMarkersData", data)
        },
        setCoordinates: (state, data) => {
            state.commit("setCoordinates", data)
        },
        setCurrentPage: (state, data) => {
            state.commit("setCurrentPage", data)
        },
        setModalData: (state, data) => {
            state.commit("setModalData", data)
        },
        setSelectedAreaRadioButton: (state, data) => {
            state.commit("setSelectedAreaRadioButton", data)
        },
        setSelectedMunicipality: (state, data) => {
            state.commit("setSelectedMunicipality", data)
        },
        setSelectedProvince: (state, data) => {
            state.commit("setSelectedProvince", data)
        },
        setLayersVisible: (state, data) => {
            state.commit("setLayersVisible", data)
        },
        setInfokarttaRadioValues: (state, data) => {
            state.commit("setInfokarttaRadioValues", data)
        },
        setLiikenneInfokarttaRadioValues: (state, data) => {
            state.commit("setLiikenneInfokarttaRadioValues", data)
        },
        setLaajakaistaJaPuhelinInfokarttaRadioValues: (state, data) => {
            state.commit("setLaajakaistaJaPuhelinInfokarttaRadioValues", data)
        },
        setPostiInfokarttaRadioValues: (state, data) => {
            state.commit("setPostiInfokarttaRadioValues", data)
        },
        setSelectedSubjectRadioButton: (state, data) => {
            state.commit("setSelectedSubjectRadioButton", data)
        },
        setTwoColumnsMobileFooter: (state, data) => {
            state.commit("setTwoColumnsMobileFooter", data)
        },
        setDataYear: (state, data) => {
            state.commit("setDataYear", data)
        },
        setPageWhitelist: (state, data) => {
            state.commit("setPageWhitelist", data)
        },
        setLegendLimits: (state, data) => {
            state.commit("setLegendLimits", data)
        },
        setIframeTitle: (state, data) => {
            state.commit("setIframeTitle", data)
        },
        setSelectedDropDownResourceKey: (state, data) => {
            state.commit("setSelectedDropDownResourceKey", data)
        },
        setTableData: (state, data) => {
            state.commit("setTableData", data)
        },
        setSubTopic: (state, data) => {
            state.commit("setSubTopic", data)
        },
        setApiData: (state, data) => {
            state.commit("setApiData", data)
        },
        setLocationSearchMinZoom: (state, data) => {
            state.commit("setLocationSearchMinZoom", data)
        },
        setFrequencySearchData: (state, data) => {
            state.commit("setFrequencySearchData", data)
        },
        setRadiomikrofonitMinFrequency: (state, data) => {
            state.commit("setRadiomikrofonitMinFrequency", data)
        },
        setRadiomikrofonitMaxFrequency: (state, data) => {
            state.commit("setRadiomikrofonitMaxFrequency", data)
        },
        setFrequencySearcValidationMessage: (state, data) => {
            state.commit("setFrequencySearcValidationMessage", data)
        },
        setTimeOfDay: (state, data) => {
            state.commit("setTimeOfDay", data)
        },
        setDataTransferDirection: (state, data) => {
            state.commit("setDataTransferDirection", data)
        },
        setOperatorList: (state, data) => {
            state.commit("setOperatorList", data)
        },
        setOperatorId: (state, data) => {
            state.commit("setOperatorId", data)
        },
        setMinMaxRadioMFrequencies: (state, data) => {
            state.commit("setMinMaxRadioMFrequencies", data)
        },
        setRadiomikrofonitBandwidth: (state, data) => {
            state.commit("setRadiomikrofonitBandwidth", data)
        },
        setMessages: (state, data) => {
            state.commit("setMessages", data)
        }
    }
})
